import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { type ReactNode } from "react";

import { isRebrandingEnabled } from "../Rebranding/workerAppRebranding";
import { PageWrapper } from "./PageWrapper";

interface Props {
  children: ReactNode;
}

/**
 * Smooth wave-like animation keyframes
 */
const animatedGradient = {
  "@keyframes smoothWave": {
    "0%": {
      transform: "translateX(-50%) translateY(-20%) rotate(0deg)",
    },
    "50%": {
      transform: "translateX(50%) translateY(20%) rotate(2deg)",
    },
    "100%": {
      transform: "translateX(-50%) translateY(-20%) rotate(0deg)",
    },
  },
  "@keyframes smoothWave2": {
    "0%": {
      transform: "translateX(30%) translateY(-30%) rotate(0deg)",
    },
    "50%": {
      transform: "translateX(-30%) translateY(30%) rotate(-2deg)",
    },
    "100%": {
      transform: "translateX(30%) translateY(-30%) rotate(0deg)",
    },
  },
};

/**
 * Animated page wrapper with smooth wave background animations.
 */
export function AnimatedBackgroundPageWrapper({ children }: Props) {
  const safeAreaPaddings = getSafeAreaPaddings({ bottom: true, fallbackBottom: "24px" });

  if (isRebrandingEnabled()) {
    // no animation for rebranding
    return (
      <PageWrapper
        sx={{
          background: (theme) => theme.palette.background.default,
          ...safeAreaPaddings,
        }}
      >
        {children}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      sx={{
        background: (theme) => theme.palette.intent?.neutral.gradient,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-100%",
          left: "-100%",
          width: "300%",
          height: "300%",
          background: `
            radial-gradient(ellipse 800px 400px at 50% 50%,
              rgba(134, 196, 252, 0.8) 0%,
              rgba(138, 194, 255, 0.3) 40%,
              transparent 70%
            )
          `,
          animation: "smoothWave 15s ease-in-out infinite",
          zIndex: 1,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: "-80%",
          left: "-80%",
          width: "260%",
          height: "260%",
          background: `
            radial-gradient(ellipse 600px 300px at 30% 70%,
              rgba(147, 183, 255, 0.8) 0%,
              rgba(138, 194, 255, 0.3) 50%,
              transparent 80%
            )
          `,
          animation: "smoothWave2 15s ease-in-out infinite",
          zIndex: 2,
        },
        ...animatedGradient,
        ...safeAreaPaddings,
      }}
    >
      {children}
    </PageWrapper>
  );
}
